import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { DashboardData, defaultAdminDashboardData, FinancialDashboardDTO } from 'app/shared/model/finance.model';
import { getStartAndEndOfMonth } from 'app/shared/util/date-utils';

const { startDate: intiStartDate, endDate: initEndDate } = getStartAndEndOfMonth();

export interface IGetEcoleParam {
  ecoleId: string;
  anneeScolaireId: string;
  startDate: string;
  endDate: string;
}

const initialState = {
  loading: true,
  errorMessage: null,
  financialDashboard: {} as FinancialDashboardDTO,
  dashboardData: defaultAdminDashboardData,
  startDate: intiStartDate,
  endDate: initEndDate,
};

const apiUrl = '/api/finance/dashboard';

export const getEcoleDashboard = createAsyncThunk(
  'finances/fetch_finance_ecole_dashboard',
  async ({ ecoleId, anneeScolaireId, startDate, endDate }: IGetEcoleParam) => {
    const requestUrl = `${apiUrl}/ecole?ecoleId=${ecoleId}&anneeScolaireId=${anneeScolaireId}&startDate=${startDate}&endDate=${endDate}`;

    const response = await axios.get<FinancialDashboardDTO>(requestUrl);
    return response.data;
  }
);

export const getAdminDashboard = createAsyncThunk('finances/fetch_finance_admin_dashboard', async () => {
  const requestUrl = `${apiUrl}/admin`;
  const response = await axios.get<DashboardData>(requestUrl);
  return response.data;
});

// Create the slice
export type FinanceState = Readonly<typeof initialState>;
const FinanceSlice = createSlice({
  name: 'finance',
  initialState: initialState as FinanceState,
  reducers: {
    reset() {
      return initialState;
    },
    setDates(state, action: PayloadAction<{ startDate: string; endDate: string }>) {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },
    resetDates(state) {
      state.startDate = intiStartDate;
      state.endDate = initEndDate;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isPending(getEcoleDashboard, getAdminDashboard), state => {
        state.errorMessage = null;
      })
      .addMatcher(isFulfilled(getEcoleDashboard), (state, action) => {
        state.loading = false;
        state.financialDashboard = action.payload;
      })
      .addMatcher(isFulfilled(getAdminDashboard), (state, action) => {
        state.loading = false;
        state.dashboardData = action.payload;
      })
      .addMatcher(isRejected(getEcoleDashboard, getAdminDashboard), (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching financial dashboard data';
      });
  },
});

export const { reset, setDates, resetDates } = FinanceSlice.actions;

export default FinanceSlice.reducer;
